import { useState } from 'react';
import {
  Box,
  Chip,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import RestoreIcon from '@mui/icons-material/Restore';
import {
  setCurrentVersion,
  getVersionDownloadUrl,
  type FileVersionDto,
} from '../../api/filesApi';
import { formatFileSize } from './formatFileSize';
import ConfirmDialog from '../../../../shared/components/ui/ConfirmDialog';
import { getErrorMessage } from '@billda/shared-ui';
import { useSnackbarStore } from '../../../../shared/stores/snackbarStore';

const dateFormatter = new Intl.DateTimeFormat('en-GB', {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
});

interface VersionHistorySectionProps {
  versions: FileVersionDto[];
  fileId: string;
  onVersionChange: () => void;
}

export function VersionHistorySection({
  versions,
  fileId,
  onVersionChange,
}: VersionHistorySectionProps) {
  const showSnackbar = useSnackbarStore(s => s.showSnackbar);
  const [confirmVersion, setConfirmVersion] = useState<FileVersionDto | null>(
    null
  );
  const [isSettingCurrent, setIsSettingCurrent] = useState(false);

  const handleSetCurrent = async (version: FileVersionDto) => {
    setIsSettingCurrent(true);
    try {
      await setCurrentVersion(fileId, version.id);
      showSnackbar(
        `Version ${version.versionNumber} is now the current version`,
        'success'
      );
      onVersionChange();
    } catch (err) {
      showSnackbar(getErrorMessage(err), 'error');
    } finally {
      setIsSettingCurrent(false);
      setConfirmVersion(null);
    }
  };

  const handleDownload = async (version: FileVersionDto) => {
    try {
      const { url } = await getVersionDownloadUrl(fileId, version.id);
      window.open(url, '_blank');
    } catch (err) {
      showSnackbar(getErrorMessage(err), 'error');
    }
  };

  if (versions.length === 0) {
    return null;
  }

  return (
    <>
      <Paper
        elevation={0}
        sx={{ border: 1, borderColor: 'divider', overflow: 'hidden' }}
      >
        {versions.map((version, index) => (
          <Box key={version.id}>
            <Box
              data-testid={`version-row-${version.id}`}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                py: 1.5,
                px: 2,
                '&:hover': { backgroundColor: 'action.hover' },
              }}
            >
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography variant="body2" fontWeight={600}>
                    v{version.versionNumber}
                  </Typography>
                  {version.isCurrent && (
                    <Chip label="Current" size="small" color="primary" />
                  )}
                </Stack>
                <Typography variant="caption" color="text.secondary">
                  {formatFileSize(version.fileSizeBytes)} &middot;{' '}
                  {version.uploadedByName} &middot;{' '}
                  {dateFormatter.format(new Date(version.uploadedAt))}
                </Typography>
                {version.notes && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: 'block', fontStyle: 'italic', mt: 0.25 }}
                  >
                    {version.notes}
                  </Typography>
                )}
              </Box>

              <Stack direction="row" spacing={0.5} sx={{ flexShrink: 0 }}>
                {!version.isCurrent && (
                  <Tooltip title="Set as current">
                    <IconButton
                      size="small"
                      onClick={() => setConfirmVersion(version)}
                      disabled={isSettingCurrent}
                      data-testid={`set-current-button-${version.id}`}
                    >
                      <RestoreIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
                <Tooltip title="Download this version">
                  <IconButton
                    size="small"
                    onClick={() => handleDownload(version)}
                    data-testid={`version-download-button-${version.id}`}
                  >
                    <DownloadIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Stack>
            </Box>
            {index < versions.length - 1 && (
              <Box sx={{ borderBottom: 1, borderColor: 'divider', mx: 2 }} />
            )}
          </Box>
        ))}
      </Paper>

      <ConfirmDialog
        open={confirmVersion !== null}
        title="Restore version?"
        message={
          confirmVersion
            ? `Set v${confirmVersion.versionNumber} as the current version? Downloads will receive this version.`
            : ''
        }
        confirmText="Set as current"
        onConfirm={() => {
          if (confirmVersion) {
            handleSetCurrent(confirmVersion);
          }
        }}
        onCancel={() => setConfirmVersion(null)}
      />
    </>
  );
}
